import { useState, useEffect } from 'react';

/**
 * Returns true when the given media query currently matches.
 * SSR-safe (defaults to false when window/matchMedia is unavailable) and
 * updates on viewport changes.
 *
 * @param {string} query - a CSS media query, e.g. '(max-width: 768px)'
 * @returns {boolean}
 */
export const useMediaQuery = (query) => {
  const getMatch = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false;

  const [matches, setMatches] = useState(getMatch);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;

    const mql = window.matchMedia(query);
    const handler = (event) => setMatches(event.matches);

    // Sync in case the query changed between render and effect
    setMatches(mql.matches);

    if (mql.addEventListener) {
      mql.addEventListener('change', handler);
      return () => mql.removeEventListener('change', handler);
    }
    // Safari < 14 fallback
    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }, [query]);

  return matches;
};
